import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, MapPin, Mail, Phone, Clock, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCookies } from "@/context/CookieContext";
import { site, whatsappLink } from "@/config/site";
import BrandLogo from "@/components/common/BrandLogo";
import NewsletterForm from "@/components/conversion/NewsletterForm";

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.05-2.82h-3.11v12.66a2.4 2.4 0 1 1-2.4-2.4c.2 0 .4.03.58.08V9.9a5.6 5.6 0 0 0-.58-.03 5.52 5.52 0 1 0 5.52 5.52V9.03a7.3 7.3 0 0 0 4.24 1.36V7.28a4.3 4.3 0 0 1-3.2-1.46Z" />
  </svg>
);

export default function Footer() {
  const { t, to, lang } = useLang();
  const { reopen } = useCookies();
  const L = site.legal;
  const homeAria = lang === "fr" ? "NEKASSUR — Retour à la page d'accueil" : "NEKASSUR — Back to homepage";

  const socials = [
    { key: "linkedin", url: site.social.linkedin, Icon: Linkedin, label: "LinkedIn" },
    { key: "instagram", url: site.social.instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook", url: site.social.facebook, Icon: Facebook, label: "Facebook" },
    { key: "tiktok", url: site.social.tiktok, Icon: TikTok, label: "TikTok" },
  ].filter((s) => s.url);

  const wLink = whatsappLink(t.whatsapp.prefill);
  const legalLinks = [t.footer.links.mentions, t.footer.links.privacy, t.footer.links.cookies, t.footer.links.complaints];
  const hours = lang === "fr" ? site.openingHours : site.openingHoursEn;

  const legalLine = `${L.legalName} · ${L.legalForm} au capital de ${L.shareCapital} · ${L.rcs} · SIREN ${L.siren} · ORIAS n° ${L.oriasNumber} · ${L.supervisoryAuthority}`;

  return (
    <footer data-testid="site-footer">
      <div className="bg-nek-light">
        <div className="nek-container py-6">
          <Link
            to={to("")}
            aria-label={homeAria}
            data-testid="footer-logo-link"
            className="inline-flex w-fit rounded-md p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nek-teal focus-visible:ring-offset-2"
          >
            <BrandLogo className="h-10 w-auto" />
          </Link>
        </div>
      </div>
      <div className="bg-nek-navy text-white">
      <div className="nek-container py-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="max-w-sm text-sm leading-relaxed text-white/70">{t.footer.tagline}</p>
            {site.flags.newsletterEnabled && <NewsletterForm />}
            {socials.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/50">{t.footer.followUs}</p>
                <div className="mt-3 flex gap-2">
                  {socials.map(({ key, url, Icon, label }) => (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`social-${key}`}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-nek-teal">
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">{t.footer.navTitle}</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              {[t.nav.particuliers, t.nav.professionnels, t.nav.epargne, t.nav.frontaliers].map((n) => (
                <li key={n.path}><Link to={to(n.path)} className="transition-colors hover:text-white">{n.label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">{t.footer.cabinetTitle}</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li><Link to={to(t.nav.about.path)} className="transition-colors hover:text-white">{t.nav.about.label}</Link></li>
              <li><Link to={to(t.nav.conseils.path)} className="transition-colors hover:text-white">{t.nav.conseils.label}</Link></li>
              <li><Link to={to("/faq")} className="transition-colors hover:text-white">{t.footer.links.faq.label}</Link></li>
              <li><Link to={to(t.nav.contact.path)} className="transition-colors hover:text-white">{t.nav.contact.label}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">{t.footer.contactTitle}</h3>
            <ul className="mt-4 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-nek-teal-100" /><span>{site.address}</span></li>
              <li className="flex items-center gap-2.5"><Phone className="h-4 w-4 shrink-0 text-nek-teal-100" /><a href={`tel:${site.phoneE164}`} className="hover:text-white">{site.phone}</a></li>
              {wLink && <li className="flex items-center gap-2.5"><MessageCircle className="h-4 w-4 shrink-0 text-nek-teal-100" /><a href={wLink} target="_blank" rel="noopener noreferrer" className="hover:text-white">WhatsApp</a></li>}
              <li className="flex items-center gap-2.5"><Mail className="h-4 w-4 shrink-0 text-nek-teal-100" /><a href={`mailto:${site.contactEmail}`} className="hover:text-white">{site.contactEmail}</a></li>
              <li className="flex items-start gap-2.5"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-nek-teal-100" /><span>{hours}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="text-xs leading-relaxed text-white/50">{legalLine}</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-white/50">© {new Date().getFullYear()} NEKASSUR. {t.footer.rights}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/60">
              {legalLinks.map((l) => (
                <Link key={l.path} to={to(l.path)} className="transition-colors hover:text-white">{l.label}</Link>
              ))}
              <button type="button" onClick={reopen} data-testid="footer-cookie-prefs" className="transition-colors hover:text-white">{t.footer.links.cookiePrefs}</button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </footer>
  );
}
